import React, {
  useContext,
} from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  RecentActors as RecentActorsIcon,
} from '@mui/icons-material';
import {
  Currency,
  GlobalProcessDuration,
  ProcessRequestLocationType,
  VisaStatus,
} from 'API';
import AutocompleteWithSearch from 'components/forms/AutocompleteWithSearch';
import DatePicker from 'components/forms/DatePicker';
import IconCircle from 'components/IconCircle';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { DICTIONARY } from 'shared/constants';
import StreetAddressDisplay from 'components/StreetAddressDisplay';
import { isValidStreetAddress } from 'shared/utilities';
import type { AppDateValue } from 'shared/datePickerTypes';
import { useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CurrencyInput from 'components/forms/CurrencyInput';

const panelStyle = {
  marginTop: '1rem',
  '& > .MuiPaper-root': {
    marginBottom: '1rem',
  },
};

const getEmploymentFieldsStyles = (theme: Theme) => ({
  card: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    width: '150px',
  },
  field: {
    marginTop: theme.spacing(1),
  },
  formLabel: {
    margin: `${theme.spacing(1)} 0`,
  },
  icon: {
    color: theme.palette.primary4,
  },
  margin: {
    margin: `${theme.spacing(2)} 0`,
  },
  radio: {
    alignSelf: 'center',
  },
  currency: {
    '& #salary-input': {
      height: '30px',
      border: 0,
      borderBottom: '2px solid #f17407',
      borderRadius: '0',
      fontSize: '1rem',
      fontFamily: 'Roboto',
      fontWeight: 400,
      color: '#3c3c41',
      width: '100%',

      '&:focus-visible': {
        outline: 'none',
        borderBottom: '2px solid #f17407',
      },
    },
  },
});

const EmploymentFields: React.FC = () => {
  const theme = useTheme();
  const styles = getEmploymentFieldsStyles(theme);
  const {
    process,
    selectedCompany,
    selectedNational,
    setProcess,
    setSelectedCompany,
  } = useContext(CreateProcessContext);

  const isShortTermGlobalProcess = process.requestedProcess === VisaStatus.GLOBAL_PROCESS
    && process.globalProcessDuration === GlobalProcessDuration.SHORT_TERM_BUSINESS_VISA;

  const handleCompanySelect = (event: any, value: any) => {
    setProcess((draft) => {
      draft.companyID = value?.id;
    });
    setSelectedCompany(value);
  };

  const salaryHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const amount = parseFloat(e.target.value.replace(/[^0-9.]/g, ''));
    const salary = {
      currency: Currency.USD,
      amount,
    };

    setProcess((draft) => {
      draft.employmentData!.salary = salary;
    });
  };

  const handleStartDateChange = (
    d: AppDateValue,
    value?: string | null | undefined,
  ) => {
    setProcess((draft) => {
      draft.employmentData!.startDate = value
        ? new Date(value).toISOString().split('T')[0]
        : value;
    });
  };

  const handleWorkingAddressChange = (event: any) => {
    setProcess((draft) => {
      draft.employmentData!.location = { type: event.target.value };
    });
  };

  return (
    <Box sx={panelStyle}>
      <IconRow>
        <IconRowCell type="icon">
          <IconCircle Icon={RecentActorsIcon} />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="h6">
            Employment Information
          </Typography>
        </IconRowCell>
      </IconRow>

      <IconRow sx={styles.margin} isInput>
        <IconRowCell type="text">
          <AutocompleteWithSearch
            label={DICTIONARY.LegalNameOfUSCompany}
            onChange={handleCompanySelect}
            type="Company"
            value={selectedCompany}
            disabled={Boolean(selectedNational?.companyID)}
            required
          />
        </IconRowCell>
      </IconRow>

      {!isShortTermGlobalProcess && (
        <IconRow sx={styles.margin} isInput>
          <IconRowCell type="text">
            <InputLabel htmlFor="salary-input" sx={styles.formLabel} required>
              Offered Salary
            </InputLabel>

            <Box sx={styles.currency}>
              <CurrencyInput id="salary-input" placeholder="$0.00" type="text" onChange={salaryHandler} />
            </Box>
          </IconRowCell>
        </IconRow>
      )}

      {!process.foreignNationalID && (
        <IconRow sx={styles.margin} isInput>
          <IconRowCell type="text">
            <FormControl
              style={{ width: '100%' }}
            >
              <DatePicker
                required
                label="Individual’s Start Date"
                value={process.employmentData?.startDate || null}
                onChange={handleStartDateChange}
              />
            </FormControl>
          </IconRowCell>
        </IconRow>
      )}

      {!isShortTermGlobalProcess && (
        <IconRow sx={{ mt: 4 }}>
          <IconRowCell type="text">
            <InputLabel htmlFor="verify-input" required>
              Physical Working Address
            </InputLabel>
            <RadioGroup
              value={process?.employmentData?.location?.type || null}
              onChange={handleWorkingAddressChange}
            >
              {selectedCompany?.data?.addressPrimary
                && isValidStreetAddress(selectedCompany?.data?.addressPrimary)
                && (
                  <FormControlLabel
                    sx={styles.formLabel}
                    value={ProcessRequestLocationType.HEADQUARTERS}
                    control={<Radio color="primary" />}
                    label={(
                      <StreetAddressDisplay
                        address={selectedCompany.data.addressPrimary}
                      />
                    )}
                  />
                )}
              {selectedCompany?.data?.addressSecondary
                && isValidStreetAddress(selectedCompany?.data?.addressSecondary)
                && (
                  <FormControlLabel
                    sx={styles.formLabel}
                    value={ProcessRequestLocationType.SECONDARY}
                    control={<Radio color="primary" />}
                    label={(
                      <StreetAddressDisplay
                        address={selectedCompany.data.addressSecondary}
                      />
                    )}
                  />
                )}
              <FormControlLabel
                sx={styles.formLabel}
                value={ProcessRequestLocationType.REMOTE}
                control={<Radio color="primary" />}
                label={<Typography>Working Remotely</Typography>}
              />
            </RadioGroup>
          </IconRowCell>
        </IconRow>
      )}
    </Box>
  );
};

export default EmploymentFields;
